'use client';

import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePathname, useRouter } from 'next/navigation';
import { hasInAppHistory } from '@/lib/nav-history';
import { skeletonFor } from '@/app/skeletons';
import { pathnameOf, standsOver, tapMountStands, useTapTarget } from './tap-mount-store';
import { PeekChromeContext } from './topbar-slot';
import styles from './modal.module.css';

/**
 * The intercepted item surface (the "peek" route mechanics). Since 2026-07-20
 * it seats INSIDE the app chrome: the sidebar and the top bar stay mounted,
 * visible, and interactive, and the peek covers the **content area only** (an
 * absolute overlay filling the shell's content column over the list, which
 * stays mounted behind). The *fullest* screen is the one where the person has
 * collapsed the sidebar - the old full-viewport opaque page (which swallowed
 * the sidebar and top bar) is gone. It keeps only the intercept's *mechanics*:
 * the list behind stays mounted, Esc is Back, and close() unwinds the soft
 * navigation that opened it (`router.back()`). There is no scrim, no
 * click-outside close, no fullscreen toggle, and no X - Back lives in the trail
 * crumbs, Esc, and (on compact) the dock's Back.
 *
 * The path trail portals into the top bar's **peek slot** (PeekChromeContext),
 * exactly as the direct-visit page portals its trail via TopbarSlot - so the
 * peek and a refresh/shared-link render are visually identical: trail in the
 * top bar, body in the content area. While the peek is mounted it flags the
 * shell (`setActive`) so the top bar swaps the underlying page's header out for
 * the peek trail.
 *
 * Not `role="dialog"`/`aria-modal`: the compact dock stays interactive above
 * this surface (Back / "…" / the Review mirror), so claiming modality would
 * lie to assistive tech. It is a page that happens to mount over another.
 *
 * `toolbar` is the path trail (portaled to the top bar); `children` is the body.
 *
 * Close is Back - except when Back would leave the app (the navigation that
 * opened the peek got *replaced* by the router race in
 * docs/archive/TECH_DEBT_RESOLVED.md "Modal close (`router.back()`) can walk
 * out of the app", or there is simply no in-app entry below): then it walks to
 * `fallbackHref`, one crumb up the trail, replacing the peek's entry, so close
 * never walks out of the app and leaves no entry to bounce back to
 * (src/lib/nav-history.ts).
 *
 * `tapMount` marks the peek drawn by the skeleton mounted by the tap
 * (`TapMount`, docs/LOADING.md §2 tool 3), before the router has committed
 * the navigation: history has no entry for it yet, so it installs no close of
 * its own (`TapMount` owns Esc and dismissal). While a tap's mount stands, a
 * peek already up underneath it takes no Esc, and on a drill from one item to
 * the next (the tap's mount then draws a peek of its own) it also yields the
 * top bar's slot, so one trail shows and one Esc does one thing.
 */
export function Modal({
  label,
  fallbackHref,
  toolbar,
  tapMount = false,
  children,
}: {
  label: string;
  /** Where close goes when Back would leave the app (one crumb up the trail). */
  fallbackHref: string;
  toolbar?: React.ReactNode;
  /** Drawn by the tap's mount, before the router commits (above). */
  tapMount?: boolean;
  children: React.ReactNode;
}) {
  const router = useRouter();
  const panelRef = useRef<HTMLDivElement>(null);
  const peek = useContext(PeekChromeContext);
  // The entry fade plays when a peek APPEARS, never when one replaces another:
  // the loading boundary's peek hands over to the page's (and one item's peek
  // to the next on a drill), and a second fade would show the list behind
  // through the frame for its whole length. Read once, at mount.
  const [enters] = useState(() => !peek?.active);
  // A tap's mount stands over this route in a peek of its own (an item or a
  // section target): that peek holds the top bar. A list or a page target
  // draws in the content column with no trail, so this peek's trail stays.
  const tapTarget = useTapTarget();
  const pathname = usePathname();
  const yields =
    !tapMount &&
    standsOver(tapTarget, pathname) &&
    ['item', 'section'].includes(skeletonFor(pathnameOf(tapTarget.href)));
  const close = useCallback(() => {
    if (hasInAppHistory()) router.back();
    else router.replace(fallbackHref);
  }, [router, fallbackHref]);

  // Flag the shell that a peek is up, so the top bar swaps its base header out
  // for the peek trail (and restores it on close). Split from the key/focus
  // effect so it re-runs only when the shell's seam identity changes.
  const setActive = peek?.setActive;
  useEffect(() => {
    if (!setActive) return;
    setActive(true);
    return () => setActive(false);
  }, [setActive]);

  // Esc is taken from the commit on: attached in a LAYOUT effect, which runs
  // in the same task as the DOM commit and before paint, so no key can land
  // between the skeleton appearing and its Esc existing. A passive effect
  // flushes after paint on a transition's commit (the route's skeleton is
  // one), and an Esc in that window reached no listener (the flaky "Esc bails
  // out of a peek that is still loading", 2026-09-30). Focus stays passive.
  useLayoutEffect(() => {
    // The tap's mount installs no close: `TapMount` owns its Esc.
    if (tapMount) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // A tap's mount stands over this peek: its Esc is the dismissal,
        // taken in the capture phase before this one (tap-mount.tsx).
        if (tapMountStands()) return;
        // A native <dialog> (Sheet, Dialog) open above the peek owns Esc; the
        // sheet stops propagation itself, but this guard holds even for a
        // dialog that forgets to.
        if (document.querySelector('dialog[open]')) return;
        // A kit Menu's anchored popover is NOT a native dialog, but while one
        // is OPEN Esc belongs to the menu - its own document listener closes it
        // and restores the trigger's focus. Without this defer, one Esc closed
        // the menu AND the peek underneath (found by the statement-menu e2e,
        // 2026-07-18).
        //
        // Openness is read from the DOM, not from where focus sits. The kit
        // Menu mounts its anchored popover only while open but moves focus in
        // one `requestAnimationFrame` LATER (menu.tsx), so a focus-based test
        // misses the whole frame between "menu is up" and "focus arrived" - and
        // an Esc landing in it closed the peek out from under an open menu. It
        // is a narrow window for a person and a coin flip for a test: it is why
        // e2e/facts.spec.ts flaked (the peek vanished, and the next click hung
        // on a detached node). A menu rendered as a compact Sheet lives inside
        // a native dialog instead, so it is excluded when that dialog is
        // CLOSED (its markup stays mounted) and caught by the guard above when
        // it is open.
        const menus = document.querySelectorAll('[role="menu"]');
        for (const menu of menus) {
          if (!menu.closest('dialog:not([open])')) return;
        }
        // Every other popover (a Select's list, a picker, a settings panel)
        // runs on `usePopover`, which marks its wrapper while open; its own
        // listener closes it, and this Esc is its alone. A control that
        // handled the Esc in React's handler (`preventDefault`: a Select
        // closing its list, a field clearing its draft) has used it too
        // (2026-10-25).
        if (e.defaultPrevented) return;
        if (document.querySelector('[data-popover-open]')) return;
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [close, tapMount]);

  useEffect(() => {
    panelRef.current?.focus();
  }, [close, tapMount]);

  // The trail lives in the top bar's peek slot (identical to the direct-visit
  // page). If the shell hasn't published the slot yet (or Modal is rendered
  // outside the shell - tests), fall back to an in-surface header so the trail
  // is never lost.
  const trailInTopbar = peek?.topbarEl && !yields ? createPortal(toolbar, peek.topbarEl) : null;

  return (
    <div
      ref={panelRef}
      className={enters ? styles.page : `${styles.page} ${styles.settled}`}
      role="region"
      aria-label={label}
      tabIndex={-1}
      data-testid="item-modal"
    >
      {trailInTopbar}
      {!peek?.topbarEl && (
        <header className={styles.head}>
          <div className={styles.toolbar}>{toolbar}</div>
        </header>
      )}
      <div className={styles.body}>{children}</div>
    </div>
  );
}
